import { SpecialDIIds } from "@kuika/kuika-cl-model";
import React from "react";
import { createPortal } from "react-dom";
import { IComments } from "../../../../models/interface/comment";
import { clipBoxToBounds } from "./comments-panel-logic";
import { IViewportBox, threadKey } from "./comments-panel-types";
import { MentionTextarea } from "./mention-textarea";

// ─── Constants ─────────────────────────────────────────────────────────────────

// Shared with comments-panel.tsx — keep sourced from the model, never hardcode "-1".
const PAGE_SCOPE_KUIKA_ID: string = SpecialDIIds.rootDI.toString();
const IFRAME_ID: string = "kuika_iframeAgent_canvas";

// The pin box is drawn down-right of its anchor, so the only thing that paints OUTSIDE the
// canvas is the selected pin's pulse ring — `comments-pin-pulse` spreads a 6px box-shadow in
// every direction (comments-panel.scss). Anchors are inset by that much so a pin sitting on the
// canvas's top/left edge can't bleed its ring over row-2 (the screen selector lives there) or
// over the left panel. Applied to every pin, not just the selected one: clamping on selection
// alone would make the pin jump the moment you click it.
const PIN_HALO_PX: number = 6;

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface ICanvasPinsProps {
  threads: IComments[];
  /** threadKey → display number (stable, keyed from full unfiltered list) */
  pinNumbers: Map<string, number>;
  selectedThreadId: string;
  onSelectThread: (threadKeyArg: string, kuikaId: string) => void;
  /** Create a new comment at a fractional anchor (fx,fy) within a clicked component. */
  onAddComment: (kuikaId: string, fx: number, fy: number, text: string) => Promise<void>;
  /** Human-readable anchor label for the click-to-add composer. */
  getComponentName: (kuikaId: string) => string;
  /** Figma-style: content for the popover shown on the pin when clicked (the panel's thread card). */
  renderThreadPopover?: (threadKey: string) => React.ReactNode;
}

interface ICanvasPinsState {
  pinPositions: IPinPosition[];
  pending: IPendingComposer | null;
  draft: string;
  submitting: boolean;
  hoverBox: IViewportBox | null;
  // threadKey of the pin whose thread popover is open (Figma-style), or "" when none.
  openThreadKey: string;
}

interface IPinPosition {
  threadKey: string;
  kuikaId: string;
  number: number;
  x: number;
  y: number;
  isResolved: boolean;
}

interface IPendingComposer {
  kuikaId: string;
  fx: number;
  fy: number;
  label: string;
  x: number;
  y: number;
  // Anchor has scrolled outside the visible canvas: keep the draft, stop painting the composer.
  offCanvas: boolean;
}

interface IKuikaHit {
  el: HTMLElement;
  kuikaId: string;
}

// ─── CanvasCommentPins ─────────────────────────────────────────────────────────
// Comment-mode overlay: pins portal to document.body positioned from the canvas iframe rects, and a click opens a Figma-style inline composer. Designer-side via same-origin iframe DOM; no agent protocol.

export class CanvasCommentPins extends React.PureComponent<ICanvasPinsProps, ICanvasPinsState> {
  private rafId: number = 0;
  private hoverRaf: number = 0;
  private unsubscribeScroll: (() => void) | null = null;
  private clickAttached: boolean = false;
  // Panel/layout resizes change the iframe size without firing window.resize, so pins re-measure off a ResizeObserver on the iframe + container.
  private resizeObserver: ResizeObserver | null = null;

  constructor(props: ICanvasPinsProps) {
    super(props);
    this.state = { pinPositions: [], pending: null, draft: "", submitting: false, hoverBox: null, openThreadKey: "" };
  }

  private attachTimers: ReturnType<typeof setTimeout>[] = [];

  componentDidMount(): void {
    this.scheduleUpdate();
    window.addEventListener("resize", this.scheduleUpdate);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
    this.listenToScrollFrame();
    // The canvas iframe may only become reachable after a few paints — retry like
    // canvas.tsx's own listener attach does.
    this.tryAttach();
    this.attachTimers = [
      setTimeout(this.tryAttach, 500),
      setTimeout(this.tryAttach, 1000),
      setTimeout(this.tryAttach, 2000)
    ];
  }

  private tryAttach = (): void => {
    this.observeCanvasResize(); // idempotent; the iframe may only exist after a few paints
    if (this.clickAttached) return;
    this.attachIframeClick();
    this.setCommentCursor(true);
  };

  // Re-measure pins whenever the canvas iframe changes size — e.g. the left panel is dragged
  // wider/narrower, which resizes the canvas column but never fires a window resize event.
  private observeCanvasResize(): void {
    if (this.resizeObserver) return;
    const iframe: HTMLIFrameElement | null = this.getIframe();
    if (!iframe) return;
    this.resizeObserver = new ResizeObserver((): void => this.scheduleUpdate());
    this.resizeObserver.observe(iframe);
    if (iframe.parentElement) this.resizeObserver.observe(iframe.parentElement);
  }

  componentDidUpdate(prevProps: ICanvasPinsProps): void {
    // Recompute only on thread-set change, NOT selection: the selection border shifts the rect ~2px, so re-snapping on every click reads as a jarring jump (highlight still updates via render).
    if (prevProps.threads !== this.props.threads) {
      this.scheduleUpdate();
    }
    // The iframe document may only become reachable after the first paints.
    if (!this.clickAttached) {
      this.attachIframeClick();
      this.setCommentCursor(true);
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener("resize", this.scheduleUpdate);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.unsubscribeScroll) this.unsubscribeScroll();
    cancelAnimationFrame(this.rafId);
    cancelAnimationFrame(this.hoverRaf);
    this.attachTimers.forEach((t: ReturnType<typeof setTimeout>) => clearTimeout(t));
    this.detachIframeClick();
    this.setCommentCursor(false);
  }

  private getIframe(): HTMLIFrameElement | null {
    return document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
  }

  private getIframeDoc(): Document | null {
    const iframe: HTMLIFrameElement | null = this.getIframe();
    if (!iframe) return null;
    try {
      return iframe.contentDocument;
    } catch {
      return null; // cross-origin guard
    }
  }

  // ─── Comment-mode affordances ─────────────────────────────────────────────────

  private setCommentCursor(on: boolean): void {
    const doc: Document | null = this.getIframeDoc();
    if (doc?.body) doc.body.style.cursor = on ? "crosshair" : "";
  }

  private attachIframeClick(): void {
    const doc: Document | null = this.getIframeDoc();
    if (!doc) return;
    // Capture phase so we read the target before the agent's own handlers; we never
    // preventDefault, so normal selection still happens alongside the composer.
    doc.addEventListener("click", this.handleIframeClick as EventListener, true);
    doc.addEventListener("mousemove", this.handleIframeMouseMove as EventListener, true);
    doc.addEventListener("mouseleave", this.handleIframeMouseLeave as EventListener, true);
    this.clickAttached = true;
  }

  private detachIframeClick(): void {
    const doc: Document | null = this.getIframeDoc();
    if (doc) {
      doc.removeEventListener("click", this.handleIframeClick as EventListener, true);
      doc.removeEventListener("mousemove", this.handleIframeMouseMove as EventListener, true);
      doc.removeEventListener("mouseleave", this.handleIframeMouseLeave as EventListener, true);
    }
    this.clickAttached = false;
  }

  // The nearest kuikaid node is often a 0×0 wrapper — walk up to the closest sized ancestor so anchoring uses the component's visible box.
  private closestSizedKuika(start: HTMLElement): IKuikaHit | null {
    let node: HTMLElement | null = start.closest("[kuikaid]") as HTMLElement | null;
    while (node) {
      const kuikaId: string | null = node.getAttribute("kuikaid");
      const rect: DOMRect = node.getBoundingClientRect();
      if (kuikaId && kuikaId !== PAGE_SCOPE_KUIKA_ID && rect.width > 0 && rect.height > 0) {
        return { el: node, kuikaId };
      }
      const parent: HTMLElement | null = node.parentElement;
      node = parent ? (parent.closest("[kuikaid]") as HTMLElement | null) : null;
    }
    return null;
  }

  // ─── Comment-mode hover highlight (Figma-style) ───────────────────────────────

  private handleIframeMouseMove = (e: MouseEvent): void => {
    const target: HTMLElement | null = e.target as HTMLElement | null;
    const hit: IKuikaHit | null = target ? this.closestSizedKuika(target) : null;
    if (!hit) {
      if (this.state.hoverBox) this.setState({ hoverBox: null });
      return;
    }
    const el: HTMLElement = hit.el;
    cancelAnimationFrame(this.hoverRaf);
    this.hoverRaf = requestAnimationFrame((): void => {
      const iframe: HTMLIFrameElement | null = this.getIframe();
      if (!iframe) return;
      const frameRect: DOMRect = iframe.getBoundingClientRect();
      const r: DOMRect = el.getBoundingClientRect();
      const scale: number = iframe.offsetWidth > 0 ? frameRect.width / iframe.offsetWidth : 1;
      // Clip to the canvas: a component taller than the visible canvas (a long page, a full-height
      // side menu) otherwise paints its highlight past the canvas edge, over the designer chrome.
      const hoverBox: IViewportBox | null = clipBoxToBounds(
        {
          x: frameRect.left + r.left * scale,
          y: frameRect.top + r.top * scale,
          width: r.width * scale,
          height: r.height * scale
        },
        frameRect
      );
      this.setState({ hoverBox });
    });
  };

  private handleIframeMouseLeave = (): void => {
    cancelAnimationFrame(this.hoverRaf);
    if (this.state.hoverBox) this.setState({ hoverBox: null });
  };

  private handleIframeClick = (e: MouseEvent): void => {
    // A canvas click while a composer is open dismisses it; composer clicks live in the top document and never reach this iframe-scoped handler.
    if (this.state.pending) {
      this.cancelPending();
      return;
    }
    // Likewise a canvas click dismisses an open thread popover (Figma "click away to close"); the
    // popover and pins live in the top document, so their own clicks never reach this handler.
    if (this.state.openThreadKey) {
      this.setState({ openThreadKey: "" });
      return;
    }
    const target: HTMLElement | null = e.target as HTMLElement | null;
    const hit: IKuikaHit | null = target ? this.closestSizedKuika(target) : null;
    if (!hit) return;
    const kuikaId: string = hit.kuikaId;
    const el: HTMLElement = hit.el;
    const iframe: HTMLIFrameElement | null = this.getIframe();
    if (!iframe) return;
    // Capture the click as a fraction of the component box so the pin stays glued to
    // that exact spot as the component moves / the canvas scrolls or zooms.
    const elRect: DOMRect = el.getBoundingClientRect();
    const fx: number = elRect.width > 0 ? (e.clientX - elRect.left) / elRect.width : 0.5;
    const fy: number = elRect.height > 0 ? (e.clientY - elRect.top) / elRect.height : 0.5;
    const frameRect: DOMRect = iframe.getBoundingClientRect();
    const scale: number = iframe.offsetWidth > 0 ? frameRect.width / iframe.offsetWidth : 1;
    this.setState({
      pending: {
        kuikaId,
        fx,
        fy,
        label: this.props.getComponentName(kuikaId) || "component",
        x: frameRect.left + e.clientX * scale,
        y: frameRect.top + e.clientY * scale,
        // Born from a click inside the canvas, so it starts on-canvas by construction.
        offCanvas: false
      },
      draft: ""
    });
  };

  private handleDocumentKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== "Escape") return;
    if (this.state.pending) {
      this.cancelPending();
    } else if (this.state.openThreadKey) {
      this.setState({ openThreadKey: "" });
    }
  };

  // ─── Inline composer ──────────────────────────────────────────────────────────

  private cancelPending = (): void => {
    this.setState({ pending: null, draft: "", submitting: false });
  };

  private submitPending = async (): Promise<void> => {
    const { pending, draft, submitting } = this.state;
    if (!pending || !draft.trim() || submitting) return;
    this.setState({ submitting: true });
    try {
      await this.props.onAddComment(pending.kuikaId, pending.fx, pending.fy, draft.trim());
      this.setState({ pending: null, draft: "", submitting: false });
    } catch {
      // Service already surfaces the error; keep the draft so it isn't lost.
      this.setState({ submitting: false });
    }
  };

  // ─── Pin position computation ─────────────────────────────────────────────────

  private listenToScrollFrame(): void {
    const doc: Document | null = this.getIframeDoc();
    if (!doc) return;
    const scrollFrame: HTMLElement | null = doc.getElementById("kuika_scrollFrame");
    if (!scrollFrame) return;
    const handler: () => void = this.scheduleUpdate;
    scrollFrame.addEventListener("scroll", handler);
    this.unsubscribeScroll = (): void => scrollFrame.removeEventListener("scroll", handler);
  }

  private scheduleUpdate = (): void => {
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(this.computePinPositions);
  };

  // A kuikaId can appear on several nested DOM nodes (a 0×0 wrapper + the real box).
  // querySelector returns the first, often the 0×0 one — pick the first sized node.
  private findSizedElement(doc: Document, kuikaId: string): Element | null {
    const candidates: NodeListOf<Element> = doc.querySelectorAll(`[kuikaid="${kuikaId}"]`);
    for (const candidate of Array.from(candidates)) {
      const rect: DOMRect = candidate.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return candidate;
    }
    return null;
  }

  private computePinPositions = (): void => {
    const doc: Document | null = this.getIframeDoc();
    const iframe: HTMLIFrameElement | null = this.getIframe();
    const positions: IPinPosition[] = [];
    const { threads, pinNumbers } = this.props;
    if (!doc || !iframe) {
      this.setState({ pinPositions: positions });
      return;
    }
    const frameRect: DOMRect = iframe.getBoundingClientRect();
    // The canvas iframe is zoom-scaled by the designer, so interior element rects
    // (unscaled) must be multiplied by the on-screen scale factor.
    const scale: number = iframe.offsetWidth > 0 ? frameRect.width / iframe.offsetWidth : 1;

    for (const thread of threads) {
      // Page-scope comments aren't anchored to a spot — they live in the panel only.
      if (thread.kuikaId === PAGE_SCOPE_KUIKA_ID) continue;

      const el: Element | null = this.findSizedElement(doc, thread.kuikaId);
      if (!el) continue; // element not visible / component deleted — skip (no broken pins)

      const elRect: DOMRect = el.getBoundingClientRect();
      const key: string = threadKey(thread);
      // Free placement when an anchor fraction is set; otherwise the top-left corner.
      const fx: number = thread.anchorFx ?? 0;
      const fy: number = thread.anchorFy ?? 0;
      const x: number = frameRect.left + (elRect.left + fx * elRect.width) * scale;
      const y: number = frameRect.top + (elRect.top + fy * elRect.height) * scale;
      // Skip pins whose anchor has scrolled out of the visible canvas viewport — otherwise the
      // pin floats over the toolbar / panel outside the canvas as the (taller) content scrolls.
      if (x < frameRect.left || x > frameRect.right || y < frameRect.top || y > frameRect.bottom) {
        continue;
      }
      positions.push({
        threadKey: key,
        kuikaId: thread.kuikaId,
        number: pinNumbers.get(key) ?? thread.order,
        // Hold the anchor a halo's width inside the canvas edges (see PIN_HALO_PX) — the cull
        // above only proves the anchor itself is inside, which still lets the selected ring
        // paint over the chrome above/left of the canvas.
        x: Math.max(x, frameRect.left + PIN_HALO_PX),
        y: Math.max(y, frameRect.top + PIN_HALO_PX),
        isResolved: thread.comments[0]?.isResolved === true
      });
    }

    this.setState({ pinPositions: positions, pending: this.repositionPending(doc, frameRect, scale) });
  };

  /**
   * Keep an open composer glued to the spot it is about to comment on.
   *
   * Its x/y are captured on click, so scrolling the canvas used to slide the anchor out from under it.
   * Re-deriving them from the anchor fraction — the same way pins are placed — makes it travel with the
   * component, and `offCanvas` hides it once that component leaves the visible canvas, exactly as its
   * pin would disappear. The draft text lives in its own state field, so nothing is lost meanwhile.
   */
  private repositionPending(doc: Document, frameRect: DOMRect, scale: number): IPendingComposer | null {
    const pending: IPendingComposer | null = this.state.pending;
    if (!pending) return null;
    const el: Element | null = this.findSizedElement(doc, pending.kuikaId);
    // Component gone (deleted, or not rendered right now): freeze the composer where it is rather than
    // guessing a new spot — the user is mid-sentence.
    if (!el) return pending;
    const r: DOMRect = el.getBoundingClientRect();
    const x: number = frameRect.left + (r.left + pending.fx * r.width) * scale;
    const y: number = frameRect.top + (r.top + pending.fy * r.height) * scale;
    const offCanvas: boolean = x < frameRect.left || x > frameRect.right || y < frameRect.top || y > frameRect.bottom;
    if (x === pending.x && y === pending.y && offCanvas === pending.offCanvas) return pending;
    return { ...pending, x, y, offCanvas };
  }

  // Anchor at the click point but flip toward the viewport center (up in the lower half, left near the right edge) so it never spills off an edge; translate(-100%) adapts as the box grows.
  private composerStyle(pending: IPendingComposer): React.CSSProperties {
    const openUp: boolean = pending.y > window.innerHeight / 2;
    const openLeft: boolean = pending.x > window.innerWidth - 248;
    const tx: string = openLeft ? "calc(-100% - 8px)" : "8px";
    const ty: string = openUp ? "calc(-100% - 8px)" : "8px";
    return { left: pending.x, top: pending.y, transform: `translate(${tx}, ${ty})` };
  }

  // Clicking a pin toggles its thread popover open/closed, and syncs the panel selection/scroll.
  private handlePinClick(pin: IPinPosition): void {
    this.props.onSelectThread(pin.threadKey, pin.kuikaId);
    this.setState((prev: ICanvasPinsState) => ({
      openThreadKey: prev.openThreadKey === pin.threadKey ? "" : pin.threadKey
    }));
  }

  // Same edge-flip logic as the composer, but sized for the wider thread card.
  private threadPopoverStyle(x: number, y: number): React.CSSProperties {
    const openUp: boolean = y > window.innerHeight / 2;
    const openLeft: boolean = x > window.innerWidth - 320;
    const tx: string = openLeft ? "calc(-100% - 12px)" : "12px";
    const ty: string = openUp ? "calc(-100% - 12px)" : "12px";
    return { left: x, top: y, transform: `translate(${tx}, ${ty})` };
  }

  render(): React.ReactNode {
    const { pinPositions, pending, draft, submitting, hoverBox, openThreadKey } = this.state;
    const { selectedThreadId, renderThreadPopover } = this.props;
    // The open thread's pin — only render its popover while that pin is on-screen (it drops out of
    // pinPositions when scrolled out of the canvas viewport or its component is gone).
    const openPin: IPinPosition | undefined = openThreadKey
      ? pinPositions.find((p: IPinPosition) => p.threadKey === openThreadKey)
      : undefined;

    return createPortal(
      <>
        {hoverBox && !pending && (
          <div
            className="comments-pin-hover"
            style={{ left: hoverBox.x, top: hoverBox.y, width: hoverBox.width, height: hoverBox.height }}
          />
        )}

        {pinPositions.map((pin: IPinPosition) => {
          const isSelected: boolean = selectedThreadId === pin.threadKey || openThreadKey === pin.threadKey;
          const className: string = [
            "comments-pin",
            pin.isResolved ? "comments-pin--resolved" : "",
            isSelected ? "comments-pin--selected" : ""
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div
              key={pin.threadKey}
              className={className}
              style={{ left: pin.x, top: pin.y }}
              onClick={(): void => this.handlePinClick(pin)}
              title={`Thread #${pin.number}`}
            >
              {pin.number}
            </div>
          );
        })}

        {openPin && renderThreadPopover && (
          <div
            className="comments-pin-thread-popover"
            style={this.threadPopoverStyle(openPin.x, openPin.y)}
            onClick={(e: React.MouseEvent): void => e.stopPropagation()}
          >
            {renderThreadPopover(openPin.threadKey)}
          </div>
        )}

        {pending && !pending.offCanvas && (
          <div
            className="comments-pin-composer"
            style={this.composerStyle(pending)}
            onClick={(e: React.MouseEvent): void => e.stopPropagation()}
          >
            <div className="comments-pin-composer__label">
              New comment · <span className="comments-pin-composer__anchor">{pending.label}</span>
            </div>
            <MentionTextarea
              className="comments-pin-composer__input"
              autoFocus
              value={draft}
              onChange={(v: string): void => this.setState({ draft: v })}
              onSubmit={(): void => {
                void this.submitPending();
              }}
              placeholder="Write a comment... (@ to mention)"
              rows={2}
            />
            <div className="comments-pin-composer__actions">
              <button className="comments-pin-composer__btn" type="button" onClick={this.cancelPending}>
                Cancel
              </button>
              <button
                className="comments-pin-composer__btn comments-pin-composer__btn--primary"
                type="button"
                disabled={!draft.trim() || submitting}
                onClick={(): void => {
                  void this.submitPending();
                }}
              >
                Comment
              </button>
            </div>
          </div>
        )}
      </>,
      document.body
    );
  }
}
